import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink } from 'lucide-react'

interface GoogleFormDialogProps {
  isOpen: boolean
  onClose: () => void
  formUrl: string
  title?: string
}

const GoogleFormDialog: React.FC<GoogleFormDialogProps> = ({ 
  isOpen, 
  onClose, 
  formUrl,
  title = 'Book Your Appointment' 
}) => {
  // Extract form ID from URL for direct form link
  const getDirectFormLink = (url: string) => {
    return url.replace('embedded=true', '').replace('viewform?', 'viewform')
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📋</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {title}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Fill out the form below to schedule your appointment
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Open in new tab button */}
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={getDirectFormLink(formUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </motion.a>
                  
                  {/* Close button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                  </motion.button>
                </div>
              </div>

              {/* Form Content */}
              <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
                <iframe
                  src={formUrl}
                  className="w-full h-full min-h-[600px]"
                  frameBorder="0"
                  marginHeight={0}
                  marginWidth={0}
                  title={title}
                >
                  Loading form...
                </iframe>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Having trouble? <a 
                      href={getDirectFormLink(formUrl)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
                    >
                      Open form in new tab
                    </a>
                  </p>
                  <button
                    onClick={onClose}
                    className="btn btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default GoogleFormDialog

